import React, { useState, useEffect, useMemo, useRef } from "react";
import { CHAINS, getActiveChains } from "../config/chains";
import {
  buildTransferRequestUrl,
  shareTransferRequest,
  MAX_NOTE_CHARS,
  noteCharCount,
} from "../utils/transferRequestLink";
import { copyTextToClipboard, canUseNativeShare } from "../utils/inviteLink";

// Palette mirrors EditorialForm (light/cream theme).
const MONO = "'JetBrains Mono', ui-monospace, Menlo, monospace";
const SANS = "'Space Grotesk', -apple-system, sans-serif";
const SERIF = "'Instrument Serif', Georgia, serif";
const INK = "#16181A";
const INK_SOFT = "#8E8E88";
const RULE = "#E6E6E1";
const RULE_SOFT = "#F2F2EE";
const ACCENT = "#56AEF0";
const ACCENT_INK = "#08222F";
const DANGER = "#C0392B";

// Network/token logo shown in the Chain/Token dropdowns — mirrors the <img>
// pattern TokenSelector uses (circular, contained, hidden if the file 404s).
const OPT_ICON = { width: 18, height: 18, borderRadius: "50%", objectFit: "contain", flexShrink: 0, display: "block" };
const hideBrokenIcon = (e) => { e.currentTarget.style.display = "none"; };

/**
 * App-styled dropdown. A native <select> can't restyle its open option list or
 * show a two-line option, so this renders a custom list: white surface, the
 * app's font, sharp corners, and an optional lighter sublabel under each label.
 * `options` = [{ value, label, sublabel? }].
 */
function Dropdown({ value, options, onChange, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          fontFamily: MONO,
          fontSize: 14,
          color: INK,
          background: "#fff",
          border: `1px solid ${RULE}`,
          borderRadius: 0,
          padding: "11px 12px",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
          {selected?.icon && (
            <img src={selected.icon} alt="" style={OPT_ICON} onError={hideBrokenIcon} />
          )}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {selected ? selected.label : "—"}
            {selected?.sublabel && (
              <span style={{ color: INK_SOFT, fontWeight: 400 }}> {selected.sublabel}</span>
            )}
          </span>
        </span>
        <span
          style={{
            color: INK_SOFT,
            fontSize: 11,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .12s",
          }}
        >
          ▾
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            background: "#fff",
            border: `1px solid ${RULE}`,
            borderRadius: 0,
            boxShadow: "0 10px 30px rgba(16,24,40,0.16)",
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {options.length === 0 && (
            <div style={{ padding: "10px 12px", fontFamily: MONO, fontSize: 13, color: INK_SOFT }}>—</div>
          )}
          {options.map((o) => {
            const isSel = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                onMouseEnter={(e) => {
                  if (!isSel) e.currentTarget.style.background = "#F7F7F4";
                }}
                onMouseLeave={(e) => {
                  if (!isSel) e.currentTarget.style.background = "#fff";
                }}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: MONO,
                  fontSize: 14,
                  color: INK,
                  textAlign: "left",
                  cursor: "pointer",
                  background: isSel ? RULE_SOFT : "#fff",
                  border: "none",
                  borderBottom: `1px solid ${RULE_SOFT}`,
                  borderRadius: 0,
                  padding: "9px 12px",
                }}
              >
                {o.icon && (
                  <img src={o.icon} alt="" style={OPT_ICON} onError={hideBrokenIcon} />
                )}
                <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
                  <span>{o.label}</span>
                  {o.sublabel && (
                    <span style={{ fontSize: 11, color: INK_SOFT, fontWeight: 400 }}>{o.sublabel}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * "Request a Payment" builder. Opens over the transfer page, pre-filled from the
 * current form (chain / token / amount) — all editable. The payment always goes to
 * the creator's own address, so there is no recipient field. Produces a shareable
 * link that pre-fills the payer's transfer form (paying the creator) after sign-in.
 */
export default function TransferRequestModal({ open, onClose, defaults }) {
  const activeChains = useMemo(() => getActiveChains(), []);
  const [to, setTo] = useState("");
  const [chainKey, setChainKey] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [genError, setGenError] = useState("");

  // Seed from the current transfer form each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setTo(defaults?.to || "");
    setChainKey(defaults?.chainKey || activeChains[0]?.id || "");
    setTokenId((defaults?.tokenId || "").toLowerCase());
    setAmount(defaults?.amount || "");
    setNote("");
    setLink("");
    setCopied(false);
    setGenError("");
  }, [open]);

  const tokens = useMemo(() => {
    const chain = CHAINS[chainKey];
    if (!chain?.tokens) return [];
    return Object.entries(chain.tokens)
      .filter(([, t]) => t.active !== false)
      .map(([id, t]) => ({ id, symbol: t.symbol, name: t.name, icon: t.icon }));
  }, [chainKey]);

  // Keep the selected token valid for the chosen chain.
  useEffect(() => {
    if (!open || tokens.length === 0) return;
    if (!tokens.some((t) => t.id === tokenId)) setTokenId(tokens[0].id);
  }, [chainKey, tokens.length]);

  // Any edit invalidates a previously-generated link → force a regenerate.
  useEffect(() => {
    if (!open) return;
    setLink("");
    setCopied(false);
    setGenError("");
  }, [to, chainKey, tokenId, amount, note]);

  // Chain options: Arbitrum One (mainnet) first, each with a lighter network sublabel.
  const chainOptions = useMemo(() => {
    const opts = activeChains.map((c) => ({
      value: c.id,
      label: c.id === "arbitrum_mainnet" ? "Arbitrum One" : c.name,
      sublabel: c.network ? `(${c.network})` : "",
      icon: c.icon,
    }));
    opts.sort((a, b) =>
      a.value === "arbitrum_mainnet" ? -1 : b.value === "arbitrum_mainnet" ? 1 : 0,
    );
    return opts;
  }, [activeChains]);
  const tokenOptions = useMemo(
    () => tokens.map((t) => ({ value: t.id, label: t.symbol, icon: t.icon })),
    [tokens],
  );

  const addrValid = /^0x[a-fA-F0-9]{40}$/.test(to.trim());
  const amtValid = Number(amount) > 0;
  const noteOver = noteCharCount(note) > MAX_NOTE_CHARS;
  const canGenerate = addrValid && !!chainKey && !!tokenId && amtValid && !noteOver;

  const onGenerate = () => {
    if (!canGenerate) return;
    try {
      setLink(
        buildTransferRequestUrl({ to: to.trim(), chainKey, tokenId, amount: amount.trim(), note }),
      );
      setGenError("");
      setCopied(false);
    } catch (e) {
      setGenError(e?.message || "Could not create link");
    }
  };
  const onCopy = async () => {
    if (!link) return;
    await copyTextToClipboard(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const onShare = async () => {
    if (!link) return;
    const symbol = tokens.find((t) => t.id === tokenId)?.symbol;
    await shareTransferRequest(link, { amount: amount.trim(), tokenSymbol: symbol, note });
  };

  if (!open) return null;

  const labelStyle = {
    display: "block",
    fontFamily: MONO,
    fontSize: 10.5,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: INK_SOFT,
    marginBottom: 6,
  };
  const fieldStyle = {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: MONO,
    fontSize: 14,
    color: INK,
    background: "#fff",
    border: `1px solid ${RULE}`,
    borderRadius: 0,
    padding: "11px 12px",
    outline: "none",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 4000,
        background: "rgba(20,22,24,0.44)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          maxHeight: "90vh",
          overflow: "visible",
          background: "#fff",
          border: `1px solid ${RULE}`,
          borderRadius: 0,
          padding: 28,
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
          fontFamily: SANS,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 400, color: INK, lineHeight: 1.1 }}>
              Request a Payment
            </div>
            <div style={{ fontFamily: MONO, fontSize: 11.5, color: INK_SOFT, marginTop: 5 }}>
              {addrValid
                ? `Paid to your wallet · ${to.slice(0, 6)}…${to.slice(-4)}`
                : "Paid to your connected wallet"}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, lineHeight: 1, color: INK_SOFT, padding: 4 }}
          >
            ×
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 18, marginTop: 22 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={labelStyle}>Chain</label>
              <Dropdown ariaLabel="Chain" value={chainKey} options={chainOptions} onChange={setChainKey} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={labelStyle}>Token</label>
              <Dropdown ariaLabel="Token" value={tokenId} options={tokenOptions} onChange={setTokenId} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Amount</label>
            <input
              value={amount}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^[0-9]*[.]?[0-9]*$/.test(v)) setAmount(v);
              }}
              placeholder="0.00"
              inputMode="decimal"
              style={fieldStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              Note (Optional)
              <span style={{ float: "right", textTransform: "none", letterSpacing: 0, color: noteOver ? DANGER : INK_SOFT }}>
                {noteCharCount(note)}/{MAX_NOTE_CHARS}
              </span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Invoice, memo, etc."
              rows={3}
              maxLength={600}
              style={{ ...fieldStyle, resize: "none", minHeight: 64 }}
            />
          </div>

          {genError ? (
            <div style={{ fontFamily: MONO, fontSize: 11, color: DANGER, marginTop: -4 }}>{genError}</div>
          ) : null}

          {!link ? (
            <button
              onClick={onGenerate}
              disabled={!canGenerate}
              style={{
                marginTop: 4,
                width: "100%",
                padding: "13px 16px",
                borderRadius: 999,
                border: "none",
                cursor: canGenerate ? "pointer" : "not-allowed",
                background: canGenerate ? ACCENT : RULE,
                color: canGenerate ? ACCENT_INK : INK_SOFT,
                fontFamily: SANS,
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              Generate Payment Link
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
              <div
                style={{
                  fontFamily: MONO,
                  fontSize: 11.5,
                  color: INK,
                  background: "#fff",
                  border: `1px solid ${RULE}`,
                  borderRadius: 0,
                  padding: "10px 12px",
                  wordBreak: "break-all",
                }}
              >
                {link}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={onCopy}
                  style={{ flex: 1, padding: "11px 14px", borderRadius: 999, border: `1px solid ${RULE}`, background: "#fff", color: INK, cursor: "pointer", fontFamily: SANS, fontWeight: 600, fontSize: 14 }}
                >
                  {copied ? "Copied" : "Copy link"}
                </button>
                {canUseNativeShare() && (
                  <button
                    onClick={onShare}
                    style={{ flex: 1, padding: "11px 14px", borderRadius: 999, border: "none", background: ACCENT, color: ACCENT_INK, cursor: "pointer", fontFamily: SANS, fontWeight: 600, fontSize: 14 }}
                  >
                    Share
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
